import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '@/store';

/**
 * The live map as a draggable bottom sheet, on phones only.
 *
 * On a desktop the map and the fleet list sit side by side and neither is in
 * the other's way. On a 390px screen they cannot both be useful at once: a
 * map tall enough to read pushes the fleet list off the bottom, and a map
 * short enough to leave room shows four streets. So the map becomes a sheet
 * the dispatcher pulls up when they want to look and drops when they want
 * the list back.
 *
 * THREE SNAP POINTS, NOT FREE DRAGGING. A sheet that stays wherever it is
 * released ends up at a height nobody chose, usually one where both halves
 * are too small. Peek is "I know it is there", half is "where is that truck",
 * full is "trace the route".
 *
 * LEAFLET HAS TO BE TOLD. Its canvas is sized when the container is, and a
 * container that changes height behind its back leaves the map rendering grey
 * bands where tiles should be — the classic symptom, and the reason
 * onHeightChange exists rather than the sheet simply animating.
 */

// Fractions of the viewport. Peek is deliberately more than a handle: a strip
// of map is what tells a dispatcher whether anything is moving at all.
const SNAP_POINTS = [0.18, 0.55, 0.92];
const DEFAULT_SNAP = 1;

// Past this the sheet goes to the next snap point rather than falling back.
const SNAP_TRAVEL_FRACTION = 0.08;

// V-308. Far smaller than SNAP_TRAVEL_FRACTION's threshold on purpose --
// this one only decides "did the pointer move at all", not "did it move
// enough to commit a snap change". Needed because the handle is both a
// drag surface and a click target: setPointerCapture (below) keeps
// pointerup targeted at this button no matter where the pointer physically
// ends up, and neither pointerdown nor pointermove ever calls
// preventDefault(), so the browser still synthesizes a plain click on this
// same button right after. Without tracking real movement separately, that
// synthetic click ran cycle() a second time on every drag -- a dispatcher
// dragging the sheet open watched it immediately snap one step past where
// they released it.
const CLICK_SUPPRESS_DEADZONE_PX = 4;

export function MapBottomSheet({ children, onHeightChange }) {
  const t = useT();
  const [snapIndex, setSnapIndex] = useState(DEFAULT_SNAP);
  const [dragOffset, setDragOffset] = useState(0);
  const drag = useRef({ pointerId: null, startY: 0 });
  const wasDraggedRef = useRef(false);
  const sheetRef = useRef(null);

  const viewportHeight = typeof window === 'undefined' ? 800 : window.innerHeight;
  const height = Math.max(
    80,
    SNAP_POINTS[snapIndex] * viewportHeight - dragOffset,
  );

  // Leaflet resizes its canvas only when told. Fired after the transition so
  // the container has actually settled at its new height, not partway.
  const notify = useCallback(() => {
    onHeightChange?.();
  }, [onHeightChange]);

  useEffect(() => {
    const timer = setTimeout(notify, 220);
    return () => clearTimeout(timer);
  }, [snapIndex, notify]);

  const onPointerDown = (event) => {
    drag.current = { pointerId: event.pointerId, startY: event.clientY };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    if (drag.current.pointerId !== event.pointerId) return;
    // Dragging up (negative dy) should grow the sheet, so the offset is
    // subtracted from the height above.
    const offset = event.clientY - drag.current.startY;
    if (Math.abs(offset) > CLICK_SUPPRESS_DEADZONE_PX) wasDraggedRef.current = true;
    setDragOffset(offset);
  };

  const finishDrag = (event) => {
    if (drag.current.pointerId !== event.pointerId) return;
    const travelled = dragOffset;
    drag.current = { pointerId: null, startY: 0 };
    setDragOffset(0);

    const threshold = viewportHeight * SNAP_TRAVEL_FRACTION;
    if (Math.abs(travelled) < threshold) return;
    // Dragged down -> smaller, dragged up -> bigger.
    const next = travelled > 0 ? snapIndex - 1 : snapIndex + 1;
    setSnapIndex(Math.max(0, Math.min(SNAP_POINTS.length - 1, next)));
  };

  const cycle = () => {
    // The click that follows a real drag (see CLICK_SUPPRESS_DEADZONE_PX
    // above) is the browser's, not the dispatcher's -- consumed once, not
    // left set for whatever the next genuine tap turns out to be.
    if (wasDraggedRef.current) {
      wasDraggedRef.current = false;
      return;
    }
    setSnapIndex((i) => (i + 1) % SNAP_POINTS.length);
  };

  return (
    <section
      ref={sheetRef}
      className="md:hidden"
      style={{ height: `${height}px`, transition: dragOffset === 0 ? 'height 200ms ease-out' : 'none' }}
      aria-label={t('mapSheet.label')}
    >
      <div className="flex h-full flex-col overflow-hidden rounded-[12px] border border-line bg-panel">
        {/* The handle is also a button. A drag gesture is invisible and
            impossible with a keyboard; tapping cycles the same three heights,
            so the sheet is operable without knowing it can be dragged. */}
        <button
          type="button"
          onClick={cycle}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={finishDrag}
          onPointerCancel={finishDrag}
          style={{ touchAction: 'none' }}
          className="flex w-full shrink-0 flex-col items-center gap-1 py-2"
          aria-label={t('mapSheet.toggle')}
          aria-expanded={snapIndex > 0}
        >
          <span aria-hidden="true" className="h-1 w-10 rounded-full bg-white/25" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.04em] text-dim">
            {t('mapSheet.title')}
          </span>
        </button>

        <div className="min-h-0 flex-1">{children}</div>
      </div>
    </section>
  );
}
